import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import AppIcon from "../../../components/ui/AppIcon";
import { useAuth } from "../../../context/AuthContext";
import api from "../../../services/api";

export default function UserProfileModal({ userId, onClose }) {
  const { user: currentUser } = useAuth();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);
  const abortControllerRef = useRef(null);
  const mountedRef = useRef(true);

  const isCurrentUser = userId === (currentUser?._id || currentUser?.id);

  useEffect(() => {
    previousFocusRef.current = document.activeElement;
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      if (previousFocusRef.current) {
        previousFocusRef.current?.focus();
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const fetchUserData = useCallback(async () => {
    if (!userId) {
      setError("No user ID provided");
      setLoading(false);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/users/${userId}`, {
        signal: abortControllerRef.current.signal,
      });

      if (!mountedRef.current) return;

      let userData = null;
      if (response && (response._id || response.id)) {
        userData = response;
      } else if (response?.success && response?.data) {
        userData = response.data;
      } else if (response?.data && (response.data._id || response.data.id)) {
        userData = response.data;
      }

      if (userData) {
        setUser(userData);
      } else {
        setError("User not found");
      }
    } catch (error) {
      if (
        !mountedRef.current ||
        error.name === "AbortError" ||
        error.code === "ERR_CANCELED"
      ) {
        return;
      }
      console.error("Error fetching user data:", error);
      setError(error.response?.data?.message || "Failed to load user data");
      setUser(null);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [userId]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getUserRole = useCallback((userData) => {
    if (userData?.user_role) {
      if (typeof userData.user_role === "object") {
        return userData.user_role.role || null;
      }
      return userData.user_role;
    }
    return userData?.role || null;
  }, []);

  const getDesignation = useCallback((userData) => {
    if (userData?.designation) {
      if (typeof userData.designation === "object") {
        return userData.designation.designation || null;
      }
      return userData.designation;
    }
    return null;
  }, []);

  const getPhone = useCallback((userData) => {
    return userData?.phoneNumber || userData?.phone || null;
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }, []);

  const userDisplayName = user?.name || user?.username || "Unknown User";
  const userRole = getUserRole(user);
  const userDesignation = getDesignation(user);
  const userAvatarFallback = `https://ui-avatars.com/api/?name=${encodeURIComponent(
    userDisplayName,
  )}&background=5E6AD2&color=fff`;
  const phone = getPhone(user);
  const userIdValue = user?._id || user?.id;
  const joinDate = user?.joinDate || user?.createdAt;
  const formattedJoinDate = formatDate(joinDate);
  const userBio = user?.bio || null;

  const renderLoading = () => (
    <div className="flex flex-col items-center py-16 px-8">
      <div className="w-28 h-28 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 animate-pulse" />
      <div className="mt-6 w-48 h-7 bg-black/5 rounded-full animate-pulse" />
      <div className="mt-2 w-36 h-5 bg-black/5 rounded-full animate-pulse" />
    </div>
  );

  const renderError = () => (
    <div className="flex flex-col items-center justify-center py-16 px-8">
      <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
        <AppIcon name="ExclamationTriangleIcon" size={28} className="text-primary" />
      </div>
      <p className="text-base text-text-secondary text-center mb-6">{error}</p>
      <button
        onClick={() => {
          setError(null);
          fetchUserData();
        }}
        className="px-6 py-2.5 rounded-xl bg-primary text-white text-sm font-medium hover:bg-primary-dark transition-all duration-200"
      >
        Try Again
      </button>
    </div>
  );

  const renderNotFound = () => (
    <div className="flex flex-col items-center justify-center py-16 px-8">
      <div className="w-16 h-16 rounded-2xl bg-black/5 flex items-center justify-center mb-4">
        <AppIcon name="UserIcon" size={28} className="text-text-muted" />
      </div>
      <p className="text-base text-text-muted">User not found</p>
    </div>
  );

  const DetailRow = ({ label, value }) => {
    if (!value) return null;
    return (
      <div className="py-3 border-b border-black/5 last:border-b-0">
        <p className="text-xs font-medium text-text-muted uppercase tracking-wide mb-1">
          {label}
        </p>
        <p className="text-sm font-semibold text-text-primary">{value}</p>
      </div>
    );
  };

  DetailRow.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
  };

  const renderUserDetails = () => {
    if (!user) return null;

    const detailsLeft = [];
    const detailsRight = [];

    detailsLeft.push({ label: "Full Name", value: user.name });
    detailsLeft.push({ label: "Username", value: user.username });
    if (isCurrentUser) {
      detailsLeft.push({ label: "Email", value: user.email });
    }
    detailsLeft.push({ label: "Role", value: userRole || null });
    detailsLeft.push({ label: "Department", value: userDesignation || null });
    
    if (isCurrentUser) {
      detailsRight.push({ label: "Phone", value: phone });
      detailsRight.push({ label: "Timezone", value: user.timezone || "Not set" });
      detailsRight.push({ label: "User ID", value: userIdValue });
      detailsRight.push({ label: "Join Date", value: formattedJoinDate });
    } else {
      detailsRight.push({ label: "Member Since", value: formattedJoinDate });
      if (userBio) {
        detailsRight.push({ label: "Bio", value: userBio });
      }
    }

    detailsRight.push({ label: "Organization", value: localStorage.getItem("org_name") || "Default" });
    if (user?.employeeCode) {
      detailsRight.push({ label: "Employee Code", value: user.employeeCode });
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-0">
          {detailsLeft.map((item, index) => (
            <DetailRow key={index} label={item.label} value={item.value} />
          ))}
        </div>
        <div className="space-y-0">
          {detailsRight.map((item, index) => (
            <DetailRow key={index} label={item.label} value={item.value} />
          ))}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) return renderLoading();
    if (error) return renderError();
    if (!user) return renderNotFound();
    return null;
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 backdrop-blur-sm p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        ref={modalRef}
        className="w-full max-w-lg bg-app-surface rounded-2xl shadow-2xl overflow-hidden transform transition-all duration-300 ease-out"
        style={{
          maxHeight: "90vh",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
        }}
      >
        <div className="relative">
          <div className="absolute inset-0 h-32 bg-gradient-to-b from-primary/5 to-transparent rounded-t-2xl" />
          
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-20 p-2 rounded-xl text-text-muted hover:text-text-primary hover:bg-black/5 transition-all duration-200"
            aria-label="Close"
          >
            <AppIcon name="XMarkIcon" size={18} />
          </button>

          {loading ? (
            renderLoading()
          ) : error ? (
            renderError()
          ) : user ? (
            <div className="px-5 sm:px-8 pt-10 pb-6">
              <div className="flex flex-col items-center">
                <div className="relative">
                  <div className="w-28 h-28 rounded-full bg-gradient-to-br from-primary/30 to-secondary/30 p-0.5">
                    <img
                      src={user.profilePhoto || userAvatarFallback}
                      className="w-full h-full rounded-full object-cover"
                      alt={userDisplayName}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = userAvatarFallback;
                      }}
                    />
                  </div>
                </div>

                <div className="mt-5 text-center">
                  <h2
                    id="modal-title"
                    className="text-2xl font-bold text-text-primary tracking-tight"
                  >
                    {userDisplayName}
                  </h2>
                  <p className="text-sm text-text-muted mt-1">
                    @{user.username || user.email?.split("@")[0] || "unknown"}
                  </p>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <div className="overflow-y-auto px-5 sm:px-8 pb-8 max-h-[50vh]">
          <div className="pt-4 border-t border-black/5">
            {renderContent()}
            {!loading && !error && user && (
              <div className="animate-fade-in">
                {renderUserDetails()}
              </div>
            )}
          </div>
        </div>

        </div>
    </div>
  );
}

UserProfileModal.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onClose: PropTypes.func.isRequired,
};